import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_URL, getAuthHeader } from '@/config';
import { toast } from 'sonner';

interface AuthContextType {
  token: string | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
}

interface User {
  id: number;
  username: string;
  email?: string;
  name?: string;
  roles: string[];
  permissions: string[];
}

interface AuthContextType {
  token: string | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch user info when token is available
  const fetchUserInfo = async (authToken: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/user`, {
        headers: getAuthHeader(authToken),
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // If token is invalid, remove it
        setToken(null);
        setUser(null);
        toast.error("Session expirée", {
          description: "Votre session a expiré. Veuillez vous reconnecter."
        });
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setToken(null);
      setUser(null);
      toast.error("Erreur de session", {
        description: "Impossible de vérifier votre session."
      });
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        await fetchUserInfo(token);
        localStorage.setItem('token', token);
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        setToken(data.token);
        setIsAuthenticated(true); // Set immediately to prevent blank page
        localStorage.setItem('token', data.token);
        // User info will be fetched by the useEffect
        return { success: true };
      } else {
        return {
          success: false,
          message: data.message || 'Authentication failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'An error occurred during authentication'
      };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: getAuthHeader(token),
        });
      }
      toast.success("Déconnexion réussie", {
        description: "Vous avez été déconnecté avec succès."
      });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error("Erreur de déconnexion", {
        description: "Une erreur est survenue lors de la déconnexion."
      });
    } finally {
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem('token');
    }
  };

  const hasPermission = (permission: string) => {
    if (!user) return false;
    // Admin has all permissions usually, but let's stick to explicit check + admin override if needed
    if (user.roles.includes('admin')) return true;
    return user.permissions.includes(permission);
  };

  const hasRole = (role: string) => {
    if (!user) return false;
    return user.roles.includes(role);
  };

  return (
    <AuthContext.Provider value={{ token, login, logout, isAuthenticated, user, loading, hasPermission, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 